import type { DropsetVariant } from "./dropsets.js";

// Proposes a starting rep/load scheme for a dropset or myorep chain,
// seeded from the working ("top") set. Pure + unit-agnostic: `topWeight`
// is whatever unit the caller is editing in (the Logger draft is in the
// row's display unit), and the returned weights come back in that same
// unit. The caller stringifies them into the draft step inputs; the
// athlete tunes from there.
//
// Literature anchors (defaults, not dogma — the user edits freely):
//   • myorep (Børge Fagerli): an activation set near failure, then short
//     "mini-sets" of a fixed small rep count at the SAME load, repeated
//     until the target can't be hit — commonly 3–5 mini-sets of 3–5 reps.
//     Default here: top + 3 clusters of 5 reps at the top load.
//   • classical dropset: 1–3 drops reducing the load ~10–30% each (≈20%
//     default), each drop taken to failure. Default: top + 2 drops at
//     80% / 60% of the top load, reps left blank (failure-dependent —
//     we don't invent a rep count we can't predict).
//
// The signature is intentionally a single options object so a future
// history-derived seeder (Plan-start defaults pulling from prior
// sessions) can extend the input without reshaping callers.

export type ProposedRound = {
  /** null = leave the input blank (e.g. classical drops are to-failure). */
  reps: number | null;
  /** null = leave the input blank (myorep clusters inherit the top load
   *  at totals time, so a blank reads as "same weight"). Same unit as
   *  the caller's `topWeight`. */
  weight: number | null;
};

export type ProposeDropsetSchemeInput = {
  variant: DropsetVariant;
  /** Top-set reps (the activation set for myorep, the working set for a
   *  classical dropset). null when not yet typed. */
  topReps: number | null;
  /** Top-set load in the caller's display unit. null when not yet typed. */
  topWeight: number | null;
};

// Default tuning constants. Kept named so the eventual settings/history
// seeder can override them in one place.
export const MYOREP_CLUSTER_COUNT = 3;
export const MYOREP_CLUSTER_REPS = 5;
const CLASSICAL_DROP_FACTORS = [0.8, 0.6] as const;

const round1 = (n: number) => Math.round(n * 10) / 10;

// Returns the FULL round list including the top round at index 0, so the
// caller can replace the chain wholesale. Always returns ≥1 round.
export const proposeDropsetScheme = (
  input: ProposeDropsetSchemeInput,
): ProposedRound[] => {
  const top: ProposedRound = {
    reps: input.topReps,
    weight: input.topWeight,
  };
  if (input.variant === "myorep") {
    const clusters: ProposedRound[] = Array.from(
      { length: MYOREP_CLUSTER_COUNT },
      () => ({
        reps: MYOREP_CLUSTER_REPS,
        // Blank weight = "same load as the activation set"; totals
        // inherit the top weight for myorep. Pre-filling avoids implying
        // the user must retype the load on every cluster.
        weight: null,
      }),
    );
    return [top, ...clusters];
  }
  // classical
  const drops: ProposedRound[] = CLASSICAL_DROP_FACTORS.map((f) => ({
    // Drops go to failure — we can't predict the rep count, so leave it
    // blank for the athlete to fill after the set.
    reps: null,
    weight:
      input.topWeight !== null && input.topWeight > 0
        ? round1(input.topWeight * f)
        : null,
  }));
  return [top, ...drops];
};
